import { useMemo, type ReactNode } from "react";
import { Grid, Paper, Stack, Typography, useMediaQuery } from "@mui/material";
import type Competence from "constants/Competence";
import competenceMap, { competencesAssociationList } from "constants/CompetenceMap";
import type { ContactMetier, ContactMetierPerimetre } from "models/ContactsMetiers";
import { ContactMetierLine } from "./ContactMetierLine";
import { grey } from "@mui/material/colors";

const workingCompetences = [...competencesAssociationList, { competence: "" }];

function isContactMetierPerimetre(
  contactMetier: ContactMetier
): contactMetier is ContactMetierPerimetre {
  return contactMetier.$type === "perimetre";
}

interface ContactsMetiersListProps {
  contactsMetiers: Array<ContactMetier>;
}

export function ContactsMetiersList({
  contactsMetiers,
}: Readonly<ContactsMetiersListProps>): ReactNode {
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up("xl"));
  const isLgScreen = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  /** Par défaut, 3 colonnes en larges: 1e colonne pour les contacts importants et les 2 suivantes pour les autres */
  const defaultColumnsFromBreakpoints = useMemo(() => {
    if (isXlScreen) {
      return 2;
    } else if (isLgScreen) {
      return 1;
    } else {
      return undefined;
    }
  }, [isXlScreen, isLgScreen]);

  if (contactsMetiers.length === 0) {
    return (
      <Typography sx={{ textAlign: "center", width: "100%" }}>
        Aucun contact métier renseigné.
      </Typography>
    );
  }

  return (
    <Grid container size={12} spacing={2}>
      {workingCompetences.map(({ competence }) => {
        const _contacts = contactsMetiers.filter((cm) => {
          if (isContactMetierPerimetre(cm)) {
            return cm?.perimetre?.competence === competence;
          } else {
            return competence === ""
              ? cm.metier.competence == null
              : cm.metier.competence === competence;
          }
        });

        // Pas de contacts métiers après filtre par compétence
        if (_contacts.length === 0) {
          return null;
        }
        const _contactsImportants = _contacts.filter((c) => c.metier.ordreAffichage != null);
        const _contactsAutres = _contacts.filter((c) => c.metier.ordreAffichage == null);

        // Ajustement du colonnage en fonction des contacts importants absents/existants
        const hasContactsImportants = _contactsImportants.length > 0;
        const hasContactsAutres = _contactsAutres.length > 0;
        const columns =
          defaultColumnsFromBreakpoints != null && !hasContactsImportants
            ? defaultColumnsFromBreakpoints + 1
            : defaultColumnsFromBreakpoints;

        // Pas de contact après filtre et séparation, pas de rendu à effectuer non plus
        if (!hasContactsImportants && !hasContactsAutres) {
          return null;
        }

        const { icon: CompetenceIcon, label: competenceLabel } =
          competenceMap.get(competence as Competence) ?? {};

        return (
          <Grid
            key={competence}
            container
            size={12}
            spacing={1}
            component={Paper}
            variant="outlined"
            sx={{
              alignContent: "flex-start",
              borderColor: competence !== "" ? `${competence.toLowerCase()}.main` : grey[400],
              borderWidth: 2,
              p: 2,
            }}>
            {CompetenceIcon != null && competenceLabel != null && (
              <Grid size={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CompetenceIcon />
                  <Typography variant="subtitle2" color={`${competence.toLowerCase()}.dark`}>
                    {competenceLabel}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {/* 1e colonne réservée aux éventuels contacts métiers importants pour l'accueil */}
            {hasContactsImportants && (
              <Grid
                size={{
                  xs: 12,
                  lg: 6 + (hasContactsAutres ? 0 : 6),
                  xl: 4 + (hasContactsAutres ? 0 : 8),
                }}>
                {_contactsImportants.map((cm) => (
                  <ContactMetierLine key={cm.id} contactMetier={cm} />
                ))}
              </Grid>
            )}
            {/* Les autres colonnes sur l'espace disponible */}
            {hasContactsAutres && (
              <Grid
                size={{
                  xs: 12,
                  lg: 6 + (hasContactsImportants ? 0 : 6),
                  xl: 8 + (hasContactsImportants ? 0 : 4),
                }}
                sx={{ columns }}>
                {_contactsAutres.map((cm) => (
                  <ContactMetierLine key={cm.id} contactMetier={cm} sx={{ breakInside: "avoid" }} />
                ))}
              </Grid>
            )}
          </Grid>
        );
      })}
    </Grid>
  );
}
